import React, { useRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  useTheme,
  Collapse,
} from '@mui/material';

import Ask from './Ask';
import AIChatHistory from './history';
import Suggestions from './Suggestions';
import ChatEmpty from './ChatEmpty';
import TranscriptLimitReached from '../transcriptLimitReached/Right';
import { RootState } from '../../../reducers';
import useLimits from '../../plan/hooks/limits';
import { deleteAskAISession } from '../selectedVideoSlice';

const Chat: React.FC = () => {
  const dispatch = useDispatch();
  const { selectedVideo } = useSelector((state: RootState) => state);
  const { view, conversation, sessionId } = selectedVideo.chat;
  const { textData, streams, _id } = selectedVideo.file || {};
  const { transcriptUrl } = textData || {};
  const isTranscriptAvailable = !!transcriptUrl;
  const guardLimits = useLimits();
  const chatBoxRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<number>(0);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [showLimitMessage, setShowLimitMessage] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = scrollPositionRef.current;
    }
  }, [view]);

  if (selectedVideo.status !== 'succeeded') {
    return null;
  }

  useEffect(() => {
    if (chatBoxRef.current) {
      const chatBox = chatBoxRef.current.querySelector('[role="log"]');
      if (chatBox) {
        chatBox.scrollTop = chatBox.scrollHeight;
      }
    }
  }, [conversation]);

  const handleDeleteConfirm = () => {
    dispatch(
      deleteAskAISession({
        fileId: _id,
        sessionId,
      })
    );
    setIsDeleteDialogOpen(false);
  };

  const handleLimitReached = () => {
    setShowLimitMessage(true);
  };

  const handleLimitMessageDismiss = () => {
    setShowLimitMessage(false);
  };

  const renderContent = () => {
    if (!isTranscriptAvailable && !(guardLimits('transcription') > 0)) {
      return <TranscriptLimitReached />;
    }

    if (!isTranscriptAvailable && !streams?.includes('audio')) {
      const { isVideoMode } = selectedVideo.chat;
      return (
        <>
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              position: 'relative',
            }}
            ref={chatBoxRef}
          >
            <Box
              sx={{
                flex: 1,
                px: 1.5,
                pt: 1.5,
                pb: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
                display: 'flex',
                flexDirection: 'column-reverse',
              }}
              role="log"
            >
              <Collapse in={!isVideoMode} timeout={200}>
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <Typography variant="h5" sx={{ mb: 1, fontWeight: 500 }}>
                    No Audio - Use Video Analysis
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Toggle the camera icon below to analyze visual content
                  </Typography>
                </Box>
              </Collapse>
              <AIChatHistory />
            </Box>
          </Box>
          <Box sx={{ mt: 0, px: 1.5, pb: 1.5 }}>
            <Ask
              chatBoxRef={chatBoxRef}
              noAudio
              onLimitReached={handleLimitReached}
              showLimitMessage={showLimitMessage}
              onLimitMessageDismiss={handleLimitMessageDismiss}
            />
          </Box>
        </>
      );
    }

    if (!isTranscriptAvailable) {
      return <ChatEmpty />;
    }

    return (
      <>
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            position: 'relative',
          }}
          ref={chatBoxRef}
        >
          <Box
            sx={{
              flex: 1,
              px: 1.5,
              pt: 1.5,
              pb: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              display: 'flex',
              flexDirection: 'column-reverse',
            }}
            role="log"
          >
            <AIChatHistory />
            <Suggestions
              chatBoxRef={chatBoxRef}
              onLimitReached={handleLimitReached}
            />
          </Box>
        </Box>
        <Box sx={{ mt: 0, px: 1.5, pb: 1.5 }}>
          <Ask
            chatBoxRef={chatBoxRef}
            onLimitReached={handleLimitReached}
            showLimitMessage={showLimitMessage}
            onLimitMessageDismiss={handleLimitMessageDismiss}
          />
        </Box>
        <Dialog
          open={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          aria-labelledby="delete-chat-dialog-title"
          aria-describedby="delete-chat-dialog-description"
        >
          <DialogTitle id="delete-chat-dialog-title">
            Clear chat history?
          </DialogTitle>
          <DialogContent>
            <Typography id="delete-chat-dialog-description">
              This will permanently delete your entire conversation history.
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setIsDeleteDialogOpen(false)}
              color="primary"
            >
              Keep
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              autoFocus
            >
              Clear
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: theme.palette.background.default,
      }}
    >
      <Card
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          height: '100%',
          border: 'none',
          borderRadius: { xs: 0, lg: '12px' },
          overflow: 'hidden',
          m: { xs: 0, md: 0, lg: 2 },
          ml: { xs: 0, md: 0, lg: 0 },
          bgcolor: theme.palette.background.paper,
          boxShadow: 'none',
        }}
      >
        <CardContent
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            p: 0,
            '&:last-child': { pb: 0 },
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              pt: 0,
              pb: 0,
            }}
          >
            {renderContent()}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Chat;
